import { Box, BoxProps, colors, Flex, Heading, Stack, Text } from "@biblioteksentralen/react";
import { ResolvedEventSummary, targetAudiences } from "@libry-content/common";
import { ResolvedService, ResolvedServiceSummary } from "@libry-content/common/src/services/groq/resolveService";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { EventsList } from "../arrangement/EventsList";
import BlockContent from "../blockContent/BlockContent";
import { DefaultContainer } from "../ContentContainer";
import { Edit } from "../editInSanity/EditInSanity";
import ContactCard from "../employees/ContactCard";
import { SanityImageWithResponsiveSize } from "../images/SanityImageWithResponsiveSize";
import { useCommonData } from "../layout/CommonDataProvider";

interface Props {
  service?: ResolvedService;
  relatedEvents?: ResolvedEventSummary[];
}

const ServiceView = ({ service, relatedEvents }: Props) => {
  const { ts } = useTranslation();

  if (!service) return null;

  return (
    <DefaultContainer>
      <Stack direction="column" spacing="2rem">
        <Stack direction="column">
          <ServiceBadges service={service} />
          <Heading as="h1">{ts(service.title)}</Heading>
          <Edit doc={service} />
          <Text fontSize="lg" maxWidth="30em">
            {ts(service.teaser)}
          </Text>
        </Stack>

        {service.image?.asset && (
          <Box paddingBottom="2rem">
            <SanityImageWithResponsiveSize
              image={service.image}
              aspectRatio={1.95}
              resolution={{ base: 540, sm: 1080, lg: 2160 }}
            />
          </Box>
        )}
        <Stack spacing="2rem" margin="auto" maxWidth="30rem">
          <BlockContent flex={1.5} blocks={ts(service.body)} />
          <Contact service={service} />
          <RelatedEvents relatedEvents={relatedEvents} />
        </Stack>
      </Stack>
    </DefaultContainer>
  );
};

export const ServiceBadges = (props: { service: ResolvedServiceSummary }) => {
  const { ts } = useTranslation();
  const { site } = useCommonData();

  const allLibrariesHasService = site?.libraryOrdering?.every(({ _ref }) =>
    props.service?.libraries?.some((library) => library._id === _ref)
  );

  const libraries = allLibrariesHasService
    ? ["Alle bibliotek"]
    : props.service?.libraries?.map((library) => ts(library.name));

  const targetAudienceLabels = props.service?.targetAudiences?.map((targetAudienceCode) =>
    ts(targetAudiences[targetAudienceCode])
  );

  const serviceTypeLabel = ts(props.service?.serviceType?.label);

  return (
    <Flex gap=".25rem" wrap="wrap">
      {libraries?.map((library) => (
        <Badge key={library} background={colors.grey80} color="white">
          {library}
        </Badge>
      ))}
      <Badge background={colors.grey30}>{serviceTypeLabel}</Badge>
      {targetAudienceLabels?.map((label) => (
        <Badge key={label} background={colors.grey15}>
          {label}
        </Badge>
      ))}
    </Flex>
  );
};

const Badge = (props: { children: React.ReactNode } & BoxProps) => (
  <Text textTransform="uppercase" fontSize="2xs" padding="0 0.5em" borderRadius="sm" {...props} />
);

const Contact = (props: Pick<Props, "service">) => {
  const { service } = props;
  const { t } = useTranslation();

  if (!service?.contact) return null;

  return (
    <Box>
      <Heading as="h2" size="md">
        {t("Kontakt")}
      </Heading>
      <ContactCard employeeOrLibrary={service.contact} />
    </Box>
  );
};

const RelatedEvents = (props: Pick<Props, "relatedEvents">) => {
  const { t } = useTranslation();

  if (!props.relatedEvents?.length) return null;

  return (
    <Stack>
      <Heading as="h2" size="md">
        {t("Relaterte arrangementer")}
      </Heading>
      <EventsList events={props.relatedEvents} />
    </Stack>
  );
};

export default ServiceView;
